import { useEffect, useState } from 'react';
import { Box, Checkbox, Grid, Group, NumberInput, RangeSlider, RangeSliderValue, TextInput } from '@mantine/core';
import { TimeInput } from '@mantine/dates';

import InputLabel from 'components/InputLabel';
import SectionTitle from 'components/SectionTitle';
import { MAX_DEFAULT_TIME_NUMBER, MAX_TIME, MAX_TIME_NUMBER, MIN_DEFAULT_TIME_NUMBER, MIN_TIME, MIN_TIME_NUMBER } from 'constants/App';
import { IPageData } from '.';
import { ContactForm } from './Contacts.types';

const CITY_DATA = require('../../assets/data/cityData.json');
const POSTAL_DATA = require('../../assets/data/postalData.json');

const AddressContact = ({
  form,
  isMobile,
  t,
}: IPageData) => {
  const [shouldSendCity, setShouldSendCity] = useState<boolean>(false);
  const [shouldSendPostal, setShouldSendPostal] = useState<boolean>(false);

  function convertTimeToNumber(timeValue: string, defaultValue: number) {
    return timeValue ? Number(timeValue.split(':')[0]) : defaultValue;
  }

  function updateTime(fieldNames: string[], value: RangeSliderValue) {
    value.forEach((val, index) => {
      const timeValue = [
        val.toString().padStart(2, '0'),
        '00'
      ].join(':');
      form.setFieldValue(fieldNames[index], timeValue);
    });
  }

  useEffect(() => {
    if (form.values.zip) {
      checkCity(form.values.zip, 'location');
    }
  }, [form.values.zip]);

  useEffect(() => {
    if (form.values.location) {
      checkPostal(form.values.location, 'zip');
    }
  }, [form.values.location]);

  function checkCity(postalValue: string, updateProperty: keyof ContactForm) {
    if (!shouldSendCity || !postalValue || postalValue.length < 4) {
      return;
    }
    const city = (POSTAL_DATA as Record<string, string>)[postalValue] || undefined;
    if (city) {
      setShouldSendPostal(false);
      form.setFieldValue(updateProperty, city);
    }
  }

  function checkPostal(cityValue: string, updateProperty: keyof ContactForm) {
    if (!shouldSendPostal || !cityValue) {
      return;
    }
    const postal = (CITY_DATA as Record<string, string>)[cityValue] || undefined;
    if (postal) {
      setShouldSendCity(false);
      form.setFieldValue(updateProperty, postal);
    }
  }

  return (
    <Box mt={48}>
      <SectionTitle title={t('section.address.contact')} />
      <Grid columns={isMobile ? 1 : 12}>
        <Grid.Col span={isMobile ? 1 : 6}>
          <NumberInput
            label={<InputLabel label={t('input.zip.label')} />}
            styles={{
              controls: { display: 'none' },
            }}
            maxLength={4}
            type={'tel'}
            key={form.key('zip')}
            {...form.getInputProps('zip')}
            onChange={(val) => {
              setShouldSendPostal(true);
              setShouldSendCity(true);
              form.setFieldValue('zip', val?.toString());
            }}
          />
        </Grid.Col>
        <Grid.Col span={isMobile ? 1 : 6}>
          <TextInput
            label={<InputLabel label={t('input.location.label')} />}
            key={form.key('location')}
            {...form.getInputProps('location')}
            onChange={(event) => {
              setShouldSendPostal(true);
              setShouldSendCity(true);
              form.setFieldValue('location', event.currentTarget.value);
            }}
          />
        </Grid.Col>
        <Grid.Col span={isMobile ? 1 : 6}>
          <TextInput
            label={<InputLabel label={t('input.street.label')} />}
            key={form.key('street')}
            {...form.getInputProps('street')}
          />
        </Grid.Col>
        <Grid.Col span={isMobile ? 1 : 6}>
          <TextInput
            label={<InputLabel label={t('input.number.label')} />}
            key={form.key('number')}
            {...form.getInputProps('number')}
          />
        </Grid.Col>
        <Grid.Col span={isMobile ? 1 : 6}>
          <InputLabel label={t('input.reachable.workdays.label')} />
          <Group justify={'space-between'}>
            <TimeInput
              w={'40%'}
              styles={{ input: { textAlign: 'center' } }}
              minTime={MIN_TIME}
              maxTime={MAX_TIME}
              key={form.key('workdayFrom')}
              {...form.getInputProps('workdayFrom')}
            />
            <TimeInput
              w={'40%'}
              styles={{ input: { textAlign: 'center' } }}
              minTime={form.values.workdayFrom}
              maxTime={MAX_TIME}
              key={form.key('workdayTo')}
              {...form.getInputProps('workdayTo')}
            />
          </Group>
          <RangeSlider
            minRange={1}
            min={MIN_TIME_NUMBER}
            max={MAX_TIME_NUMBER}
            step={1}
            defaultValue={[
              convertTimeToNumber(form.values.workdayFrom, MIN_DEFAULT_TIME_NUMBER),
              convertTimeToNumber(form.values.workdayTo, MAX_DEFAULT_TIME_NUMBER),
            ]}
            value={[
              convertTimeToNumber(form.values.workdayFrom, MIN_DEFAULT_TIME_NUMBER),
              convertTimeToNumber(form.values.workdayTo, MAX_DEFAULT_TIME_NUMBER),
            ]}
            mt={10}
            label={null}
            color={'#378F0E'}
            styles={{ thumb: { backgroundColor: '#62AF45', border: 'none' } }}
            onChange={(value) => updateTime(['workdayFrom', 'workdayTo'], value)}
          />
        </Grid.Col>
        {form.values.saturdayAvailability ? 
          <Grid.Col span={isMobile ? 1 : 6}>
            <InputLabel label={t('input.reachable.saturday.label')} />
            <Group justify={'space-between'}>
              <TimeInput
                w={'40%'}
                styles={{ input: { textAlign: 'center' } }}
                disabled={!form.values.saturdayAvailability}
                minTime={MIN_TIME}
                maxTime={MAX_TIME}
                key={form.key('saturdayFrom')}
                {...form.getInputProps('saturdayFrom')}
              />
              <TimeInput
                w={'40%'}
                styles={{ input: { textAlign: 'center' } }}
                disabled={!form.values.saturdayAvailability}
                minTime={form.values.saturdayFrom}
                maxTime={MAX_TIME}
                key={form.key('saturdayTo')}
                {...form.getInputProps('saturdayTo')}
              />
            </Group>
            <RangeSlider
              minRange={1}
              min={MIN_TIME_NUMBER}
              max={MAX_TIME_NUMBER}
              step={1}
              defaultValue={[
                convertTimeToNumber(form.values.saturdayFrom, MIN_DEFAULT_TIME_NUMBER),
                convertTimeToNumber(form.values.saturdayTo, MAX_DEFAULT_TIME_NUMBER),
              ]}
              value={[
                convertTimeToNumber(form.values.saturdayFrom, MIN_DEFAULT_TIME_NUMBER),
                convertTimeToNumber(form.values.saturdayTo, MAX_DEFAULT_TIME_NUMBER),
              ]}
              mt={10}
              label={null}
              color={'#378F0E'}
              styles={{ thumb: { backgroundColor: '#62AF45', border: 'none' } }}
              disabled={!form.values.saturdayAvailability}
              onChange={(value) => updateTime(['saturdayFrom', 'saturdayTo'], value)}
            />
          </Grid.Col>
        : undefined}
        <Grid.Col span={isMobile ? 1 : 12}>
          <Checkbox
            color={'#62AF45'}
            label={<InputLabel label={t('input.reachable.saturday.label')} />}
            key={form.key('saturdayAvailability')}
            {...form.getInputProps('saturdayAvailability')}
          />
        </Grid.Col>
      </Grid>
    </Box>
  )
}

export default AddressContact;